import { c as _c } from "react/compiler-runtime";
import type { Address, BigIntish } from '@morpho-org/blue-sdk';
import { type InterfaceToken, toBigInt } from '@morpho-org/dapp-core';
import { scrollToElementAfterDelay } from '@/helpers/scrollToElement';
import type { VaultV2Caps, VaultV2PendingTimelockDecodedData, VaultV2Timelocks } from '../types/pendingTimelocks';
import { VaultV2PendingTimelockAbdicate, VaultV2PendingTimelockAddAdapter, VaultV2PendingTimelockAddress, VaultV2PendingTimelockChangeAllocator, VaultV2PendingTimelockChangeCap, VaultV2PendingTimelockChangeForceDeallocatePenalty, VaultV2PendingTimelockChangePerformanceFee, VaultV2PendingTimelockChangeTimelock, VaultV2PendingTimelockCountdown, VaultV2PendingTimelockRemoveAdapter } from './components';
import VaultV2PendingTimelockChangeManagementFee from './components/VaultV2PendingTimelockChangeManagementFee';
import { StyledVaultPendingTimelock } from './VaultV2PendingTimelockStyled';
const SCROLL_CONFIG = {
  exposure: {
    elementId: 'exposure-section'
  },
  timelock: {
    elementId: 'timelocks-section'
  },
  fees: {
    elementId: 'fees-section'
  },
  riskCuration: {
    elementId: 'risk-curation-section'
  }
} as const;
interface VaultV2PendingTimelockProps {
  validAt: BigIntish;
  functionName: string;
  decodedData: VaultV2PendingTimelockDecodedData;
  caps?: VaultV2Caps;
  vaultSupplyToken?: InterfaceToken | null;
  timelocks?: VaultV2Timelocks[];
  disableNavigation?: boolean;
  currentPerformanceFee?: number | null;
  currentManagementFee?: number | null;
  currentManagementFeeRecipient?: Address | null;
  currentPerformanceFeeRecipient?: Address | null;
}
const VaultV2PendingTimelock = t0 => {
  const $ = _c(75);
  const {
    caps: t1,
    decodedData,
    functionName,
    validAt,
    timelocks: t2,
    disableNavigation: t3,
    currentPerformanceFee,
    currentManagementFee,
    currentManagementFeeRecipient,
    currentPerformanceFeeRecipient,
    vaultSupplyToken
  } = t0;
  let t4;
  if ($[0] !== t1) {
    t4 = t1 === undefined ? [] : t1;
    $[0] = t1;
    $[1] = t4;
  } else {
    t4 = $[1];
  }
  const caps = t4;
  let t5;
  if ($[2] !== t2) {
    t5 = t2 === undefined ? [] : t2;
    $[2] = t2;
    $[3] = t5;
  } else {
    t5 = $[3];
  }
  const timelocks = t5;
  const disableNavigation = t3 === undefined ? false : t3;
  const isSelectable = !disableNavigation && (decodedData.__typename === "VaultV2AdapterPendingData" || decodedData.__typename === "VaultV2TimelockPendingData" || decodedData.__typename === "VaultV2IncreaseCapPendingData" || decodedData.__typename === "VaultV2SetPerformanceFeePendingData" || decodedData.__typename === "VaultV2SetPerformanceFeeRecipientPendingData" || decodedData.__typename === "VaultV2SetManagementFeeRecipientPendingData" || decodedData.__typename === "VaultV2SetIsAllocatorPendingData");
  let t6;
  if ($[4] !== decodedData.__typename || $[5] !== isSelectable) {
    t6 = () => {
      if (!isSelectable) {
        return;
      }
      let config = undefined;
      bb45: switch (decodedData.__typename) {
        case "VaultV2SetIsAllocatorPendingData":
          {
            config = SCROLL_CONFIG.riskCuration;
            break bb45;
          }
        case "VaultV2AdapterPendingData":
        case "VaultV2IncreaseCapPendingData":
          {
            config = SCROLL_CONFIG.exposure;
            break bb45;
          }
        case "VaultV2TimelockPendingData":
          {
            config = SCROLL_CONFIG.timelock;
            break bb45;
          }
        case "VaultV2SetPerformanceFeeRecipientPendingData":
        case "VaultV2SetManagementFeeRecipientPendingData":
        case "VaultV2SetPerformanceFeePendingData":
          {
            config = SCROLL_CONFIG.fees;
          }
      }
      if (!config) {
        return;
      }
      scrollToElementAfterDelay(config.elementId);
    };
    $[4] = decodedData.__typename;
    $[5] = isSelectable;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  const handleClick = t6;
  let t7;
  if ($[7] !== decodedData || $[8] !== functionName) {
    t7 = decodedData.__typename === "VaultV2AbdicatePendingData" && <VaultV2PendingTimelockAbdicate functionName={functionName} decodedData={decodedData} />;
    $[7] = decodedData;
    $[8] = functionName;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  let t8;
  if ($[10] !== decodedData || $[11] !== functionName) {
    t8 = decodedData.__typename === "VaultV2AdapterPendingData" && functionName === "AddAdapter" && <VaultV2PendingTimelockAddAdapter functionName={functionName} decodedData={decodedData} />;
    $[10] = decodedData;
    $[11] = functionName;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  let t9;
  if ($[13] !== decodedData || $[14] !== functionName) {
    t9 = decodedData.__typename === "VaultV2AdapterPendingData" && functionName === "RemoveAdapter" && <VaultV2PendingTimelockRemoveAdapter functionName={functionName} decodedData={decodedData} />;
    $[13] = decodedData;
    $[14] = functionName;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  let t10;
  if ($[16] !== decodedData.__typename || $[17] !== decodedData.functionName || $[18] !== decodedData.timelock || $[19] !== functionName || $[20] !== timelocks) {
    t10 = decodedData.__typename === "VaultV2TimelockPendingData" && <VaultV2PendingTimelockChangeTimelock newValue={decodedData.timelock} functionName={functionName} decodedFunctionName={decodedData.functionName} timelocks={timelocks.map(_temp)} />;
    $[16] = decodedData.__typename;
    $[17] = decodedData.functionName;
    $[18] = decodedData.timelock;
    $[19] = functionName;
    $[20] = timelocks;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  let t11;
  if ($[22] !== decodedData || $[23] !== functionName) {
    t11 = decodedData.__typename === "VaultV2SetIsAllocatorPendingData" && <VaultV2PendingTimelockChangeAllocator functionName={functionName} decodedData={decodedData} />;
    $[22] = decodedData;
    $[23] = functionName;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  let t12;
  if ($[25] !== currentManagementFeeRecipient || $[26] !== decodedData.__typename || $[27] !== decodedData.managementFeeRecipient || $[28] !== functionName) {
    t12 = decodedData.__typename === "VaultV2SetManagementFeeRecipientPendingData" && <VaultV2PendingTimelockAddress newValue={decodedData.managementFeeRecipient} functionName={functionName} currentValue={currentManagementFeeRecipient ?? undefined} />;
    $[25] = currentManagementFeeRecipient;
    $[26] = decodedData.__typename;
    $[27] = decodedData.managementFeeRecipient;
    $[28] = functionName;
    $[29] = t12;
  } else {
    t12 = $[29];
  }
  let t13;
  if ($[30] !== currentPerformanceFeeRecipient || $[31] !== decodedData.__typename || $[32] !== decodedData.performanceFeeRecipient || $[33] !== functionName) {
    t13 = decodedData.__typename === "VaultV2SetPerformanceFeeRecipientPendingData" && <VaultV2PendingTimelockAddress newValue={decodedData.performanceFeeRecipient} functionName={functionName} currentValue={currentPerformanceFeeRecipient ?? undefined} />;
    $[30] = currentPerformanceFeeRecipient;
    $[31] = decodedData.__typename;
    $[32] = decodedData.performanceFeeRecipient;
    $[33] = functionName;
    $[34] = t13;
  } else {
    t13 = $[34];
  }
  let t14;
  if ($[35] !== decodedData.__typename || $[36] !== decodedData.forceDeallocatePenalty || $[37] !== functionName) {
    t14 = decodedData.__typename === "VaultV2SetForceDeallocatePenaltyPendingData" && <VaultV2PendingTimelockChangeForceDeallocatePenalty newValue={decodedData.forceDeallocatePenalty} functionName={functionName} />;
    $[35] = decodedData.__typename;
    $[36] = decodedData.forceDeallocatePenalty;
    $[37] = functionName;
    $[38] = t14;
  } else {
    t14 = $[38];
  }
  let t15;
  if ($[39] !== caps || $[40] !== decodedData || $[41] !== functionName || $[42] !== vaultSupplyToken) {
    t15 = decodedData.__typename === "VaultV2IncreaseCapPendingData" && <VaultV2PendingTimelockChangeCap functionName={functionName} decodedData={decodedData} caps={caps} vaultSupplyToken={vaultSupplyToken} />;
    $[39] = caps;
    $[40] = decodedData;
    $[41] = functionName;
    $[42] = vaultSupplyToken;
    $[43] = t15;
  } else {
    t15 = $[43];
  }
  let t16;
  if ($[44] !== currentManagementFee || $[45] !== decodedData.__typename || $[46] !== decodedData.managementFee || $[47] !== functionName) {
    t16 = decodedData.__typename === "VaultV2SetManagementFeePendingData" && <VaultV2PendingTimelockChangeManagementFee newValue={decodedData.managementFee} currentValue={currentManagementFee ?? undefined} functionName={functionName} />;
    $[44] = currentManagementFee;
    $[45] = decodedData.__typename;
    $[46] = decodedData.managementFee;
    $[47] = functionName;
    $[48] = t16;
  } else {
    t16 = $[48];
  }
  let t17;
  if ($[49] !== currentPerformanceFee || $[50] !== decodedData.__typename || $[51] !== decodedData.performanceFee || $[52] !== functionName) {
    t17 = decodedData.__typename === "VaultV2SetPerformanceFeePendingData" && <VaultV2PendingTimelockChangePerformanceFee currentValue={currentPerformanceFee ?? undefined} newValue={decodedData.performanceFee} functionName={functionName} />;
    $[49] = currentPerformanceFee;
    $[50] = decodedData.__typename;
    $[51] = decodedData.performanceFee;
    $[52] = functionName;
    $[53] = t17;
  } else {
    t17 = $[53];
  }
  let t18;
  if ($[54] !== validAt) {
    t18 = <VaultV2PendingTimelockCountdown validAt={validAt} />;
    $[54] = validAt;
    $[55] = t18;
  } else {
    t18 = $[55];
  }
  let t19;
  if ($[56] !== decodedData.__typename || $[57] !== functionName || $[58] !== handleClick || $[59] !== t10 || $[60] !== t11 || $[61] !== t12 || $[62] !== t13 || $[63] !== t14 || $[64] !== t15 || $[65] !== t16 || $[66] !== t17 || $[67] !== t18 || $[68] !== t7 || $[69] !== t8 || $[70] !== t9) {
    t19 = <div onClick={handleClick} data-function-name={functionName} data-typename={decodedData.__typename}>{t7}{t8}{t9}{t10}{t11}{t12}{t13}{t14}{t15}{t16}{t17}{t18}</div>;
    $[56] = decodedData.__typename;
    $[57] = functionName;
    $[58] = handleClick;
    $[59] = t10;
    $[60] = t11;
    $[61] = t12;
    $[62] = t13;
    $[63] = t14;
    $[64] = t15;
    $[65] = t16;
    $[66] = t17;
    $[67] = t18;
    $[68] = t7;
    $[69] = t8;
    $[70] = t9;
    $[71] = t19;
  } else {
    t19 = $[71];
  }
  let t20;
  if ($[72] !== isSelectable || $[73] !== t19) {
    t20 = <StyledVaultPendingTimelock.Container selectable={isSelectable}>{t19}</StyledVaultPendingTimelock.Container>;
    $[72] = isSelectable;
    $[73] = t19;
    $[74] = t20;
  } else {
    t20 = $[74];
  }
  return t20;
};
export default VaultV2PendingTimelock;
function _temp(timelock) {
  return {
    functionName: timelock.functionName,
    duration: toBigInt(timelock.duration)
  };
}